// src/components/EntityFilterBar.tsx
import React, { useState, useRef, useEffect } from 'react';

export interface FilterEntity {
  entityId: string;
  name: string;
  type: 'CHARACTER' | 'LOCATION' | 'EVENT';
  noteCount: number;
}

interface EntityFilterBarProps {
  entities: FilterEntity[];
  activeFilters: string[];
  onToggle: (entityId: string) => void;
  onClear: () => void;
}

const INLINE_COUNT = 3;

const TYPE_LABEL: Record<'CHARACTER' | 'LOCATION' | 'EVENT', string> = {
  CHARACTER: 'Characters', LOCATION: 'Locations', EVENT: 'Events',
};

const EntityFilterBar: React.FC<EntityFilterBarProps> = ({ entities, activeFilters, onToggle, onClear }) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [search, setSearch] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!dropdownOpen) return;
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [dropdownOpen]);

  if (entities.length === 0) return null;

  const sorted = [...entities].sort((a, b) => b.noteCount - a.noteCount);
  const inlineSlots = sorted.slice(0, INLINE_COUNT);
  const overflow = sorted.slice(INLINE_COUNT);

  // Active filters not already in the inline slots surface to the top
  const extraActive = entities.filter(
    e => activeFilters.includes(e.entityId) && !inlineSlots.find(s => s.entityId === e.entityId)
  );

  const filteredOverflow = overflow.filter(
    e => !search || e.name.toLowerCase().includes(search.toLowerCase())
  );

  const renderChip = (e: FilterEntity, showX: boolean) => {
    const active = activeFilters.includes(e.entityId);
    return (
      <span
        key={e.entityId}
        style={{
          background: active ? '#0abfbc' : '#f0f0f0',
          border: active ? 'none' : '1px solid #ddd',
          color: active ? '#fff' : '#555',
          borderRadius: 12, padding: '2px 8px', fontSize: 10,
          fontWeight: active ? 700 : 400, cursor: 'pointer',
          whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 3,
        }}
        onClick={() => onToggle(e.entityId)}
      >
        {e.name}
        {active && showX && <span style={{ opacity: 0.7, fontSize: 9 }}>✕</span>}
      </span>
    );
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'nowrap' }}>
      <span style={{ fontSize: 10, color: '#888', whiteSpace: 'nowrap' }}>Filter:</span>

      {extraActive.map(e => renderChip(e, true))}
      {inlineSlots.map(e => renderChip(e, true))}

      {overflow.length > 0 && (
        <div style={{ position: 'relative' }} ref={dropdownRef}>
          <span
            style={{
              background: '#f0f0f0', border: '1px solid #ddd', color: '#555',
              borderRadius: 12, padding: '2px 9px', fontSize: 10,
              cursor: 'pointer', fontWeight: 600, whiteSpace: 'nowrap',
            }}
            onClick={() => setDropdownOpen(o => !o)}
          >
            +{overflow.length} ▾
          </span>

          {dropdownOpen && (
            <div style={{
              position: 'absolute', top: '110%', left: 0, background: '#fff',
              border: '1px solid #e0e0e0', borderRadius: 8,
              boxShadow: '0 4px 16px rgba(0,0,0,.12)',
              padding: 8, minWidth: 210, zIndex: 999,
            }}>
              <div style={{ fontSize: 9, fontWeight: 700, color: '#aaa', textTransform: 'uppercase', letterSpacing: '.05em', padding: '2px 6px 6px' }}>
                All entities on this board
              </div>
              <div style={{ display: 'flex', alignItems: 'center', background: '#f5f5f5', borderRadius: 4, padding: '4px 7px', marginBottom: 6 }}>
                <span style={{ color: '#aaa', fontSize: 10, marginRight: 4 }}>🔍</span>
                <input
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search..."
                  style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: 10, width: '100%' }}
                />
              </div>

              {(['CHARACTER', 'LOCATION', 'EVENT'] as const).map(type => {
                const items = filteredOverflow.filter(e => e.type === type);
                if (items.length === 0) return null;
                return (
                  <div key={type}>
                    <div style={{ fontSize: 9, color: '#999', padding: '4px 6px 3px', textTransform: 'uppercase', letterSpacing: '.04em', borderTop: '1px solid #f0f0f0', marginTop: 2 }}>
                      {TYPE_LABEL[type]}
                    </div>
                    {items.map(e => {
                      const active = activeFilters.includes(e.entityId);
                      return (
                        <div
                          key={e.entityId}
                          style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 6px', borderRadius: 4, background: active ? '#eef5ff' : undefined, cursor: 'pointer' }}
                          onClick={() => onToggle(e.entityId)}
                        >
                          <span style={{ fontSize: 10, fontWeight: active ? 700 : 400, color: active ? '#0abfbc' : undefined }}>
                            {active ? '✓ ' : ''}{e.name}
                          </span>
                          <span style={{ fontSize: 9, color: active ? '#0abfbc' : '#aaa' }}>
                            {e.noteCount} notes
                          </span>
                        </div>
                      );
                    })}
                  </div>
                );
              })}

              {activeFilters.length > 0 && (
                <div style={{ borderTop: '1px solid #f0f0f0', marginTop: 6, paddingTop: 6, display: 'flex', justifyContent: 'flex-end' }}>
                  <span
                    style={{ fontSize: 10, color: '#aaa', cursor: 'pointer' }}
                    onClick={() => { onClear(); setDropdownOpen(false); }}
                  >
                    Clear filter
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {activeFilters.length > 0 && (
        <span style={{ fontSize: 10, color: '#0abfbc', cursor: 'pointer', marginLeft: 4 }} onClick={onClear}>
          Clear
        </span>
      )}
    </div>
  );
};

export default EntityFilterBar;
